/**
 * regionMarkup: a REGION is a markup that is not an annotation
 * (app/regions-as-markups.md).
 *
 * A region's shape is drawn, selected, edited and commented by the markup
 * engine, but it lives in the REGIONS store (ProjectRegions: live, shared,
 * compare-and-swap), never in the PDF's annotation layer. The two stores are
 * different databases, so every place that could write one must know which
 * it is holding. This module is the one answer.
 *
 *   region markup = an ordinary CLOSED markup + `region: { recordId, typeId }`
 *
 * `isRegionMarkup(m)` is THE predicate. Every PDF path (save, download,
 * bake, flatten, print), the unsaved flag and the markup Record diff ask it
 * and leave region markups out; the regions bridge asks it and writes only
 * region markups to the regions store. The server's save route refuses them
 * too (backend: /api/pdf/save-markups), so a missed client path still never
 * reaches the file.
 *
 * Pure: no React, no storage. Pinned by regionMarkup.eval.js.
 */

import { findRecord, makeRecord, recordScopeOf } from '../../utils/regionRecords.js';
import { subRegionLookOf, lookScopeOf } from './regionTypeLook.js';

/** THE predicate: this markup belongs to the regions store, not the PDF. */
export const isRegionMarkup = (m) => !!(m && m.region && typeof m.region === 'object');

/** A list with every region markup taken out (what a PDF path may send). */
export function withoutRegionMarkups(list) {
  if (!Array.isArray(list)) return list;
  if (!list.some(isRegionMarkup)) return list;
  return list.filter(m => !isRegionMarkup(m));
}

/** Only the region markups of a list. */
export const onlyRegionMarkups = (list) => (Array.isArray(list) ? list.filter(isRegionMarkup) : []);

// The closed shapes a region can be. An open line cannot enclose anything.
const CLOSED_BOX = new Set(['rectangle', 'circle', 'cloud']);
const CLOSED_POLY = new Set(['polygon', 'cloudPolyline', 'polyline']);

/** Can this markup be a region? Closed shapes only. */
export function canBeRegion(m) {
  if (!m || m.fromPdf || m.readOnly) return false;
  if (CLOSED_BOX.has(m.type)) return true;
  if (m.type === 'polygon') return Array.isArray(m.points) && m.points.length >= 3;
  if (CLOSED_POLY.has(m.type)) return !!m.closed && Array.isArray(m.points) && m.points.length >= 3;
  return false;
}

/**
 * Can this markup be kept WITH a region at all? Any markup of ours: a closed
 * shape joins as a PART (it outlines the region's area); anything else (a
 * line, an arrow, a text box, a stamp, a measurement) joins as a NOTE, kept
 * with the region in the regions store and never in the PDF, but never
 * counted as area. Not an imported markup (copy it first), not a Cut or a
 * redaction (they edit the page), not a flattened or read-only one.
 */
export function canJoinRegion(m) {
  if (!m || m.fromPdf || m.readOnly || m.flattened || m.cut) return false;
  if (m.type === 'redact' || m.type === 'native') return false;
  return true;
}

/** A region markup that outlines area (a part), as against a note. */
export const isRegionPart = (m) => isRegionMarkup(m) && m.region.role !== 'note';

/**
 * Put a markup into a region: `region` set, as a part when it is a closed
 * shape and as a note otherwise. Returns null for a markup that cannot join
 * (an IMPORTED one: its annotation stays in the file, so it would sit in
 * both databases; copy it first, a copy is ours).
 */
export function toRegionMarkup(m, { recordId, typeId = null }) {
  if (!canJoinRegion(m) || !recordId) return null;
  const role = canBeRegion(m) ? 'part' : 'note';
  const out = { ...m, region: { recordId, typeId: typeId || null, ...(role === 'note' ? { role } : {}) } };
  delete out.modified;
  return out;
}

/** A region markup back to a plain markup (Make Plain Markup, and exports). */
export function toPlainMarkup(m) {
  if (!isRegionMarkup(m)) return m;
  const out = { ...m };
  delete out.region;
  return out;
}

/**
 * The geometry readers (objects inside, search, Ask) understand, from a
 * region markup: { shapeType: 'rectangle' | 'circle' | 'polyline', bbox,
 * polylinePoints }, page-normalised, the same shape the old region records
 * carried, so regionGeometry.isObjectInRegion works on it unchanged.
 */
export function regionGeometryOf(m) {
  if (!m || (isRegionMarkup(m) && m.region.role === 'note')) return null;
  const pts = Array.isArray(m.points) ? m.points.filter(p => p && Number.isFinite(p.x) && Number.isFinite(p.y)) : null;
  if (pts && pts.length >= 3) {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
    return {
      shapeType: 'polyline',
      bbox: { x: x0, y: y0, width: x1 - x0, height: y1 - y0 },
      polylinePoints: pts.map(p => ({ x: p.x, y: p.y })),
    };
  }
  const { startX, startY, endX, endY } = m;
  if (![startX, startY, endX, endY].every(Number.isFinite)) return null;
  const bbox = {
    x: Math.min(startX, endX), y: Math.min(startY, endY),
    width: Math.abs(endX - startX), height: Math.abs(endY - startY),
  };
  return { shapeType: m.type === 'circle' ? 'circle' : 'rectangle', bbox, polylinePoints: null };
}

// ── The bridge between the two stores ─────────────────────────────────────

/**
 * What changed among the REGION markups of the markup store since the last
 * sync: { upserts, removedIds }. `synced` is the region markups as the two
 * stores last agreed (by id). A markup that is the same object is unchanged.
 */
export function regionMarkupDelta(synced, current) {
  const before = new Map();
  for (const m of synced || []) if (isRegionMarkup(m) && m.id != null) before.set(String(m.id), m);
  const upserts = [];
  const seen = new Set();
  for (const m of current || []) {
    if (!isRegionMarkup(m) || m.id == null) continue;
    const id = String(m.id);
    seen.add(id);
    if (before.get(id) !== m) upserts.push(m);
  }
  const removedIds = [];
  for (const id of before.keys()) if (!seen.has(id)) removedIds.push(id);
  return { upserts, removedIds };
}

// Region markups that ARRIVED from the regions store (the load, or another
// person's change), as opposed to ones drawn in this tab. An undo snapshot
// taken before one arrived does not hold it, and replaying that snapshot
// must not read as "delete it": the bridge would delete it from the server
// for everyone. One drawn here is still undone like any markup.
const arrivedRegionIds = new Set();
export function noteRegionMarkupsArrived(list) {
  for (const m of list || []) if (isRegionMarkup(m) && m.id != null) arrivedRegionIds.add(String(m.id));
}

/**
 * The store's markups for `file` replaced by a history snapshot's, without
 * the snapshot ever removing a region markup it never knew about. Every
 * other file's markups are left as they are now.
 */
export function restoreFileSnapshot(prev, snapshot, file) {
  const inSnapshot = new Set();
  for (const m of snapshot) if (m && m.filename === file && m.id != null) inSnapshot.add(String(m.id));
  return [
    ...prev.filter(m => m.filename !== file
      || (isRegionMarkup(m) && !inSnapshot.has(String(m.id)) && arrivedRegionIds.has(String(m.id)))),
    ...snapshot.filter(m => m.filename === file),
  ];
}

/**
 * The markup store with its region markups replaced by `regionMarkups` (the
 * regions store's), keeping every other markup and each region markup's
 * place where it already had one. Returns `markups` itself when nothing
 * would change, so a setter can skip the write.
 */
export function mergeRegionMarkupsInto(markups, regionMarkups) {
  const list = Array.isArray(markups) ? markups : [];
  const incoming = new Map();
  for (const m of regionMarkups || []) if (isRegionMarkup(m) && m.id != null) incoming.set(String(m.id), m);
  let changed = false;
  const placed = new Set();
  const out = [];
  for (const m of list) {
    if (!isRegionMarkup(m)) { out.push(m); continue; }
    const id = String(m.id);
    const next = incoming.get(id);
    if (!next) { changed = true; continue; }      // gone from the regions store
    placed.add(id);
    if (next !== m) changed = true;
    out.push(next);
  }
  for (const [id, m] of incoming) {
    if (placed.has(id)) continue;
    changed = true;
    out.push(m);
  }
  return changed ? out : list;
}

/**
 * For the unsaved flag: did this commit change the file's PLAIN markups, only
 * its region markups, or nothing of it at all? 'plain' | 'region' | 'none'.
 * Compared by object identity (the store updates immutably).
 */
export function fileChangeKind(prev, next, filename) {
  const split = (list) => {
    const plain = [];
    const region = [];
    for (const m of list || []) {
      if (!m || m.filename !== filename) continue;
      (isRegionMarkup(m) ? region : plain).push(m);
    }
    return { plain, region };
  };
  const same = (a, b) => a.length === b.length && a.every((m, i) => m === b[i]);
  const a = split(prev);
  const b = split(next);
  if (!same(a.plain, b.plain)) return 'plain';
  if (!same(a.region, b.region)) return 'region';
  return 'none';
}

/**
 * Strip region markups from a /api/pdf/save-markups request body (a JSON
 * string). Returns the body unchanged when it carries none. The fetch
 * interceptor runs every save request through this, so a PDF path that
 * forgot the filter still never sends a region.
 */
export function stripRegionsFromSaveBody(body) {
  if (typeof body !== 'string' || body.indexOf('"region":{') === -1) return { body, stripped: 0 };
  let parsed;
  try { parsed = JSON.parse(body); } catch { return { body, stripped: 0 }; }
  if (!parsed || !Array.isArray(parsed.markups)) return { body, stripped: 0 };
  const kept = parsed.markups.filter(m => !isRegionMarkup(m));
  const stripped = parsed.markups.length - kept.length;
  if (!stripped) return { body, stripped: 0 };
  return { body: JSON.stringify({ ...parsed, markups: kept }), stripped };
}

/**
 * Convert to Region: make each markup a part of a sub-region of ONE type.
 * A markup of ours becomes the region in place (a flag, not a conversion:
 * same id, same shape, same undo step). An IMPORTED one stays where it is
 * in the file and a COPY becomes the region (`copyOf` makes the copy, so the
 * copy is ours). The sub-region is found by name, or made.
 *
 * Returns { updates: Map(id -> region markup), added: [region markups],
 * records, createdRecords, skipped }.
 */
export function convertToRegionMarkups({
  markups, types, records, regionType, subRegionName,
  autoIncrement = false, autoIncrementStart = 1, stateId = null,
  useAnnotationStyle = true, fillColor, borderColor, fillOpacity,
  by = null, copyOf = null,
}) {
  const type = (types || []).find(t => t && t.name === regionType) || null;
  let recs = Array.isArray(records) ? records : [];
  const createdRecords = [];
  // A document-scoped type: one record per document (recordScopeOf).
  const docScoped = recordScopeOf(type) === 'document';
  const recordFor = (name, filename) => {
    const file = docScoped ? (filename || null) : null;
    let rec = findRecord(recs, types, regionType, name, file);
    if (!rec) {
      rec = makeRecord({ typeId: (type && type.id) || null, typeName: type && type.id ? null : regionType, name, filename: file, by });
      recs = [...recs, rec];
      createdRecords.push(rec);
    }
    return rec;
  };
  const updates = new Map();
  const added = [];
  let skipped = 0;
  let n = Number(autoIncrementStart) || 1;
  for (const m of markups || []) {
    const source = m && m.fromPdf ? (copyOf ? copyOf(m) : null) : m;
    if (!source || !canJoinRegion(source) || isRegionMarkup(m)) { skipped++; continue; }
    const name = autoIncrement ? `${subRegionName} ${n++}` : subRegionName;
    const rec = recordFor(name, source.filename);
    // AN EXISTING MARKUP KEEPS ITS COLOURS (useAnnotationStyle, the
    // default): putting a markup into a region is filing, not restyling —
    // the type's look applies as DEFAULTS when something NEW is drawn into
    // the region (regionTarget), never to what was already on the drawing.
    // It used to take the region look whenever the type's look was
    // type-scoped or the sub-region already existed, so a right-click
    // "Add to <sub-region>" recoloured the markup. Where the type gives
    // each sub-region its own look AND the sub-region is new, the markup's
    // colours additionally BECOME that sub-region's look, so its swatch
    // agrees with what joined. useAnnotationStyle false restyles to the
    // region's look (or the caller's raw colours), for callers that ask.
    const isPart = canBeRegion(source);
    const fresh = createdRecords.includes(rec);
    let style;
    if (useAnnotationStyle) {
      if (type && fresh && lookScopeOf(type) === 'subRegion') {
        rec.look = isPart
          ? { color: source.color, strokeOpacity: source.strokeOpacity ?? 1, fillColor: source.fillColor, fillOpacity: source.fillOpacity, lineStyle: source.lineStyle }
          : { color: source.color };
      }
      style = {};
    } else if (type) {
      const look = subRegionLookOf(rec, type, { isPart });
      style = {};
      for (const [k, v] of Object.entries(look)) if (v !== undefined) style[k] = v;
    } else {
      style = !isPart ? {} : {
        ...(borderColor ? { color: borderColor } : {}),
        ...(fillColor ? { fillColor } : {}),
        ...(fillOpacity !== undefined ? { fillOpacity } : {}),
      };
    }
    const part = toRegionMarkup({ ...source, ...style, ...(stateId ? { stateId } : {}) }, { recordId: rec.id, typeId: (type && type.id) || null });
    if (!part) { skipped++; continue; }
    if (source === m) updates.set(m.id, part);
    else added.push(part);
  }
  return { updates, added, records: recs, createdRecords, skipped };
}
